import { useState, useCallback } from 'react';
import axios from 'axios';
import { client } from '../sdkAdapter';
import { getStoredToken } from '../utils/authStorage';

const API_BASE = (import.meta as any).env?.VITE_API_BASE || 'http://127.0.0.1:8000';

// Aggregated SEO entry returned by the new backend endpoint
export interface SeoEntry {
    id: string;           // composite: 'prod_45', 'cat_3', 'seo_12', etc.
    entity_id: number;
    entity_type: 'product' | 'category' | 'brand' | 'seo';
    url: string;
    name: string;
    meta_title: string;
    meta_description: string;
    seo_id: number | null; // null = no SEO configured yet
    created_at?: string;
}

// Legacy type kept for modal compatibility
export interface SeoData {
    id: number;
    url: string;
    meta_title: string;
    meta_keyword: string;
    meta_description: string;
    created_at?: string;
    updated_at?: string;
}

export interface SeoFormData {
    url: string;
    meta_title: string;
    meta_keyword: string;
    meta_description: string;
    entity_type?: string;
    entity_id?: number;
}

export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
    error?: string;
}

export type SeoFilterType = 'all' | 'product' | 'category' | 'brand' | 'seo';

export function useSeoData() {
    const [seos, setSeos] = useState<SeoEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchSeos = useCallback(async (type: SeoFilterType = 'all') => {
        setLoading(true);
        setError(null);
        try {
            const token = getStoredToken();
            // Use axios directly so we can send `type` as a real query param
            const response = await axios.get(`${API_BASE}/api/attributes/seos`, {
                params: type !== 'all' ? { type } : {},
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                },
            });

            let data: SeoEntry[] = [];
            const payload = response.data;
            if (Array.isArray(payload)) {
                data = payload;
            } else if (Array.isArray(payload?.data)) {
                data = payload.data;
            } else if (Array.isArray(payload?.data?.data)) {
                data = payload.data.data;
            }

            setSeos(Array.isArray(data) ? data : []);
            return data;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch SEO data';
            setError(errorMessage);
            console.error('Error fetching SEOs:', err);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    return { seos, loading, error, fetchSeos, setSeos };
}

export function useSeoCreate() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const createSeo = useCallback(async (formData: SeoFormData): Promise<SeoData | null> => {
        setLoading(true);
        setError(null);
        try {
            const response = await (client as any).postApiAttributesSeoCreate({ postApiAttributesSeoCreateRequest: formData } as any) as any;

            let createdSeo = null;
            if (response && typeof response === 'object') {
                createdSeo = response.data?.data || response.data || response;
                if (createdSeo && Object.keys(createdSeo).length === 0) {
                    createdSeo = response.data?.data?.data || null;
                }
            }

            if (!createdSeo || (typeof createdSeo === 'object' && Object.keys(createdSeo).length === 0)) {
                setError('Failed to retrieve created SEO data from server - empty response');
                return null;
            }

            return createdSeo;
        } catch (err) {
            const anyErr: any = err;
            let errorMessage = 'Failed to create SEO';
            try {
                if (anyErr.response && anyErr.response.data) {
                    const respData = anyErr.response.data;
                    if (respData.errors) {
                        const messages = Object.values(respData.errors).flat();
                        errorMessage = (messages as string[]).join(' ');
                    } else if (respData.message) {
                        errorMessage = respData.message;
                    } else {
                        errorMessage = JSON.stringify(respData);
                    }
                } else if (anyErr.message) {
                    errorMessage = anyErr.message;
                }
            } catch (parseErr) {
                console.error('Error parsing error response:', parseErr);
            }
            setError(errorMessage);
            console.error('Error creating SEO:', anyErr);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    return { createSeo, loading, error };
}

export function useSeoUpdate() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const updateSeo = useCallback(async (id: number, formData: SeoFormData): Promise<SeoData | null> => {
        setLoading(true);
        setError(null);
        try {
            const response = await (client as any).postApiAttributesSeoUpdateId({ id: String(id), postApiAttributesSeoUpdateIdRequest: formData } as any) as any;

            const updatedSeo = response.data?.data || response.data;

            if (!updatedSeo) {
                setError('Failed to retrieve updated SEO data from server');
                return null;
            }

            return updatedSeo;
        } catch (err) {
            const anyErr: any = err;
            let errorMessage = 'Failed to update SEO';
            try {
                if (anyErr.response && anyErr.response.data) {
                    const respData = anyErr.response.data;
                    if (respData.errors) {
                        const messages = Object.values(respData.errors).flat();
                        errorMessage = (messages as string[]).join(' ');
                    } else if (respData.message) {
                        errorMessage = respData.message;
                    } else {
                        errorMessage = JSON.stringify(respData);
                    }
                } else if (anyErr.message) {
                    errorMessage = anyErr.message;
                }
            } catch (parseErr) {
                console.error('Error parsing error response:', parseErr);
            }
            setError(errorMessage);
            console.error('Error updating SEO:', anyErr);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    return { updateSeo, loading, error };
}

export function useSeoDelete() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const deleteSeo = useCallback(async (id: number): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            await (client as any).postApiAttributesSeoDelete({ postV1FooterDeleteRequest: { id } } as any) as any;
            return true;
        } catch (err) {
            const anyErr: any = err;
            let errorMessage = 'Failed to delete SEO';
            try {
                if (anyErr.response && anyErr.response.data) {
                    const respData = anyErr.response.data;
                    if (respData.errors) {
                        const messages = Object.values(respData.errors).flat();
                        errorMessage = (messages as string[]).join(' ');
                    } else if (respData.message) {
                        errorMessage = respData.message;
                    } else {
                        errorMessage = JSON.stringify(respData);
                    }
                } else if (anyErr.message) {
                    errorMessage = anyErr.message;
                }
            } catch (parseErr) {
                console.error('Error parsing error response:', parseErr);
            }
            setError(errorMessage);
            console.error('Error deleting SEO:', anyErr);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    return { deleteSeo, loading, error };
}
